import React, { useState, useEffect, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { FiArrowRight, FiX } from 'react-icons/fi';
import { TUTORIAL_STEPS } from './steps';
import {
  Highlight,
  Tooltip,
  TooltipTitle,
  TooltipDesc,
  TooltipFooter,
  StepCounter,
  ButtonRow,
  PrimaryButton,
  SkipButton,
  CompleteButton,
} from './styles';

const DEFAULT_PADDING = 8;
const TOOLTIP_GAP = 16;
const DEFAULT_WIDTH = 320;
const COMPLETE_WIDTH = 420;

/**
 * Calcula a posição do balão relativa ao retângulo do alvo, respeitando o
 * placement desejado e mantendo o balão dentro da viewport.
 */
function computeTooltipPosition(rect, placement, padding, tooltipWidth) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let top = 0;
  let left = 0;

  switch (placement) {
    case 'bottom':
      top = rect.bottom + padding + TOOLTIP_GAP;
      left = rect.left + rect.width / 2 - tooltipWidth / 2;
      break;
    case 'top':
      top = rect.top - padding - TOOLTIP_GAP;
      left = rect.left + rect.width / 2 - tooltipWidth / 2;
      break;
    case 'right':
      top = rect.top + rect.height / 2;
      left = rect.right + padding + TOOLTIP_GAP;
      break;
    case 'left':
      top = rect.top + rect.height / 2;
      left = rect.left - padding - TOOLTIP_GAP - tooltipWidth;
      break;
    default:
      top = rect.bottom + padding + TOOLTIP_GAP;
      left = rect.left + rect.width / 2 - tooltipWidth / 2;
  }

  // Manter dentro da viewport (margem de 12px)
  left = Math.max(12, Math.min(left, vw - tooltipWidth - 12));
  top = Math.max(12, Math.min(top, vh - 12));

  return { top, left };
}

export default function DemoTour({ steps = TUTORIAL_STEPS, onFinish }) {
  const [current, setCurrent] = useState(0);
  const [rect, setRect] = useState(null);

  const step = steps[current];
  const padding = step?.padding ?? DEFAULT_PADDING;

  // Mede o elemento alvo e reposiciona em resize/scroll
  const measure = useCallback(() => {
    if (!step) return;
    const el = document.querySelector(step.selector);
    if (!el) {
      setRect(null);
      return;
    }
    const r = el.getBoundingClientRect();
    setRect({
      top: r.top,
      left: r.left,
      width: r.width,
      height: r.height,
      bottom: r.bottom,
      right: r.right,
    });
  }, [step]);

  useLayoutEffect(() => {
    measure();
  }, [measure]);

  useEffect(() => {
    const handler = () => measure();
    window.addEventListener('resize', handler);
    window.addEventListener('scroll', handler, true);
    // Remede após transições/layout assentar
    const t = setTimeout(measure, 120);
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('scroll', handler, true);
      clearTimeout(t);
    };
  }, [measure]);

  const finish = useCallback(() => {
    onFinish && onFinish();
  }, [onFinish]);

  const next = useCallback(() => {
    if (current < steps.length - 1) {
      setCurrent((c) => c + 1);
    } else {
      finish();
    }
  }, [current, steps.length, finish]);

  // Ao entrar no passo da Tela de Arquivos, seleciona o dashboard 'files'
  // pelo mesmo mecanismo de navegação do menu lateral e abre a tela de upload
  useEffect(() => {
    if (step?.id === 'display') {
      window.dispatchEvent(new CustomEvent('navigate-dashboard', { detail: 'files' }));
      window.dispatchEvent(new CustomEvent('demo-open-files-upload'));
    }
  }, [step?.id]);

  // Suporte a teclado: Esc pula, Enter/→ avança
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') finish();
      if (e.key === 'Enter' || e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [finish, next]);

  if (!step) return null;

  const isLast = current === steps.length - 1;
  const tooltipWidth = step?.showCompleteButton ? COMPLETE_WIDTH : DEFAULT_WIDTH;

  // Fallback: se o alvo não for encontrado, centraliza o balão sem spotlight
  const highlightStyle = rect
    ? {
        top: rect.top - padding,
        left: rect.left - padding,
        width: rect.width + padding * 2,
        height: rect.height + padding * 2,
      }
    : null;

  const tooltipPos = rect
    ? computeTooltipPosition(rect, step.placement, padding, tooltipWidth)
    : { top: window.innerHeight / 2 - 80, left: window.innerWidth / 2 - tooltipWidth / 2 };

  return createPortal(
    <>
      {highlightStyle ? (
        <Highlight style={highlightStyle} />
      ) : (
        // Sem alvo: overlay escuro simples cobrindo a tela
        <Highlight
          style={{ top: 0, left: 0, width: 0, height: 0 }}
        />
      )}

      <Tooltip style={{ top: tooltipPos.top, left: tooltipPos.left, width: tooltipWidth }}>
        <TooltipTitle>{step.title}</TooltipTitle>
        <TooltipDesc>{step.description}</TooltipDesc>
        <TooltipFooter>
          <StepCounter>
            {current + 1} de {steps.length}
          </StepCounter>
          <ButtonRow>
            <SkipButton onClick={finish}>
              <FiX size={14} /> Pular
            </SkipButton>
            {step?.showCompleteButton && (
              <CompleteButton onClick={finish}>
                Tutorial completo
              </CompleteButton>
            )}
            <PrimaryButton onClick={next}>
              {isLast ? 'Concluir' : 'Próximo'}
              {!isLast && <FiArrowRight size={14} />}
            </PrimaryButton>
          </ButtonRow>
        </TooltipFooter>
      </Tooltip>
    </>,
    document.body
  );
}
